import { Pipe, PipeTransform } from '@angular/core';
import {
  flattenSubjectiveStoryLabels,
  isHeadingOnlyLine,
  stripMarkdownHorizontalRules
} from '../utils/soap-section-format.util';
import { flattenAssessmentSectionText, flattenPlanSectionText } from '../utils/soap-assessment-plan.util';
import { foldObjectiveForDisplay } from '../utils/soap-objective-format.util';
import { foldPatientInstructionsForDisplay } from '../utils/soap-patient-instructions-format.util';

export type SoapNormalizeSection = 'subjective' | 'objective' | 'assessment' | 'plan' | 'patientInstructions' | 'other';

/**
 * Display-only normalization for SOAP text:
 * - Removes the extra blank line between a header and its first content line.
 *   Example:
 *     "Identifying Information\n\nThe patient ..." -> "Identifying Information\n  The patient ..."
 * - In Assessment, strips "Problem X" prefixes from headings.
 * - In Plan, strips category labels (Medications, Diagnostics ordered, …) and uses plain bullets.
 *
 * This pipe is intentionally conservative and does not attempt markdown/HTML rendering.
 */
@Pipe({
  name: 'soapNormalize',
  standalone: true,
  pure: true
})
export class SoapNormalizePipe implements PipeTransform {
  transform(value: string | null | undefined, section: SoapNormalizeSection = 'other'): string {
    if (!value) {
      return '';
    }

    let text = stripMarkdownHorizontalRules(value.replace(/\r/g, ''));

    if (section === 'assessment') {
      text = this.stripProblemNumberPrefixes(text);
      text = flattenAssessmentSectionText(text);
    }

    if (section === 'plan') {
      text = flattenPlanSectionText(text);
      text = this.normalizePlanBullets(text);
    }

    if (section === 'patientInstructions') {
      return foldPatientInstructionsForDisplay(text);
    }

    if (section === 'subjective') {
      text = flattenSubjectiveStoryLabels(text);
    } else if (section === 'objective') {
      text = foldObjectiveForDisplay(text);
    }

    text = this.compactHeaderToFirstContentLine(text);

    return text;
  }

  /** Convert "   - Label: content" to "• Label: content" so soapToHtml renders Plan like Assessment. */
  private normalizePlanBullets(text: string): string {
    return text.replace(/^\s*-\s+/gm, '• ');
  }

  private stripProblemNumberPrefixes(text: string): string {
    const lines = text.split('\n');
    const out: string[] = [];

    for (const raw of lines) {
      if (raw == null) {
        continue;
      }

      // Preserve leading indentation + bullet if present.
      const m = raw.match(/^(\s*)(•\s+)?(.*)$/);
      const indent = m?.[1] ?? '';
      const bullet = m?.[2] ?? '';
      let rest = (m?.[3] ?? '').trimStart();

      // Remove "Problem 1:" / "Problem 1 -" / "Problem 1 –" / "Problem 1 " prefixes.
      rest = rest.replace(/^problem\s+\d+\s*[:\-–—]\s*/iu, '');
      rest = rest.replace(/^problem\s+\d+\s+(?=\S)/iu, '');

      out.push(`${indent}${bullet}${rest}`);
    }

    return out.join('\n');
  }

  private compactHeaderToFirstContentLine(text: string): string {
    const lines = text.split('\n');
    const out: string[] = [];

    const isBlank = (s: string) => s.trim() === '';
    const isHeadingLine = (line: string): boolean => {
      const t = line.trim();
      if (!t) return false;

      // Treat bullet headings as headings too.
      const withoutBullet = t.startsWith('• ') ? t.slice(2).trimStart() : t;
      const withoutNumber = withoutBullet.replace(/^\d{1,2}\.\s+/, '');
      const h = withoutNumber.trim();

      // Don't treat sentence-like lines as headings.
      if (/[.?!]$/.test(h)) return false;

      // Heuristic: short-ish, contains letters, and not obviously a list item.
      if (h.length > 90) return false;
      if (!/[A-Za-z]/.test(h)) return false;
      if (/^[-*]\s+/.test(h)) return false;

      // Most headings are a few words (e.g. "Chief complaint (CC)").
      const wordCount = h.split(/\s+/).filter(Boolean).length;
      return wordCount >= 1 && wordCount <= 12;
    };

    for (let i = 0; i < lines.length; i++) {
      const current = lines[i] ?? '';

      // If this is a header, drop any immediate blank lines after it and indent the first content line.
      if (isHeadingLine(current)) {
        out.push(current.trimEnd());

        // Heading-only sections (e.g. "Social History:") keep a following bullet list intact.
        if (isHeadingOnlyLine(current)) {
          continue;
        }

        // Count consecutive blank lines right after the header.
        let j = i + 1;
        while (j < lines.length && isBlank(lines[j] ?? '')) {
          j++;
        }

        // If we skipped at least one blank line and there's content, pull it up.
        if (j > i + 1 && j < lines.length) {
          const next = lines[j] ?? '';
          const nextTrimmed = next.trim();
          // Indent content under header unless it's already indented.
          const indented = /^\s+/.test(next) ? nextTrimmed : `  ${nextTrimmed}`;
          out.push(indented);
          i = j; // we've consumed up through the content line; loop will i++ to continue after it
          continue;
        }

        continue;
      }

      out.push(current);
    }

    return out.join('\n');
  }
}

